import { useCallback } from "react"
import {
  formatISO,
  parseISO,
  getHours,
  getMinutes,
  isBefore,
  isAfter,
  startOfWeek,
  endOfWeek,
} from "date-fns"
import { toast } from "sonner"
import { useIsMutating } from "@tanstack/react-query"
import type { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import isARecurringEvent from "@/modules/calendar/utils/is-a-recurring-event"
import isLocked from "@/modules/calendar/utils/is-locked"
import {
  hasRecurringEventOnSameDay,
  isMovedToDifferentDay,
  isOutOfAllowedRange,
} from "@/modules/calendar/utils/calendar-events"
import useUpdateEvent from "@/modules/calendar/queries/use-update-event"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"
import useEventModal from "@/modules/calendar/stores/use-event-modal"

const parseTimeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(":").map(Number)
  return hours * 60 + minutes
}

export default function useCalendarEvents() {
  const { currentWeek, ranges, setCurrentDate } = useCalendarConfig()
  const { startDate, endDate } = ranges
  const { mutateAsync } = useUpdateEvent()
  const { openEventModal } = useEventModal()
  const isPending = useIsMutating({ mutationKey: ["update-event"] }) > 0

  const handleEventUpdate = useCallback(
    async (calendarEvent: UserEvent, newStart: Date, newEnd: Date, type: "drop" | "resize") => {
      if (isLocked(calendarEvent) || isPending) {
        return false
      }

      // If the date/time did not change, we do nothing.
      const originalStart = formatISO(parseISO(calendarEvent.startDate))
      const originalEnd = formatISO(parseISO(calendarEvent.endDate))
      const newStartISO = formatISO(newStart)
      const newEndISO = formatISO(newEnd)

      if (originalStart === newStartISO && originalEnd === newEndISO) {
        return false
      }

      // Validates that it ‘moves or manipulates’ to a valid time.
      const newStartTime = getHours(newStart) * 60 + getMinutes(newStart)
      const newEndTime = getHours(newEnd) * 60 + getMinutes(newEnd)
      const startTime = parseTimeToMinutes(ranges.startTime)
      const endTime = parseTimeToMinutes(ranges.endTime)

      if (newStartTime < startTime || newEndTime > endTime) {
        return false
      }

      // Validates that it ‘moves or manipulates’ to a valid day.
      const minDate = parseISO(startDate)
      const maxDate = parseISO(endDate)

      if (isOutOfAllowedRange(newStart, newEnd, minDate, maxDate)) {
        toast.error("No se puede mover eventos fuera del rango permitido.")
        return false
      }

      // Validate that you do not move recurring on invalid days.
      if (
        type === "drop" &&
        isARecurringEvent(calendarEvent) &&
        isMovedToDifferentDay(calendarEvent, newStart, newEnd) &&
        hasRecurringEventOnSameDay(calendarEvent, newStart, newEnd)
      ) {
        toast.error(
          "No puede mover un evento recurrente al mismo día que otro evento del mismo tipo.",
        )
        return false
      }

      if (isARecurringEvent(calendarEvent)) {
        const event = {
          id: calendarEvent.id,
          startDate: formatISO(newStart),
          endDate: formatISO(newEnd),
          day: calendarEvent.day,
          parent: calendarEvent.parent,
        }

        openEventModal({ type: "edition", event })
        return
      }

      try {
        const body = {
          title: calendarEvent.parent.title,
          startDate: formatISO(newStart),
          endDate: formatISO(newEnd),
          category: calendarEvent.parent.category,
          recurringDays: calendarEvent.parent.days,
          isBulkUpdate: calendarEvent.parent.days.length > 0,
        }

        const payload = {
          eventId: calendarEvent.id,
          body,
          params: {
            returnStart: extractISODate(currentWeek.startDate),
            returnEnd: extractISODate(currentWeek.endDate),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }

        await mutateAsync(payload)
        const isRecurring = calendarEvent.parent.days.length > 0
        toast.success(isRecurring ? "Los eventos han sido editados" : "El evento ha sido editado")
        return true
      } catch (_) {
        return false
      }
    },
    [startDate, endDate, currentWeek, mutateAsync],
  )

  const handleEventInteraction = useCallback(
    async ({
      event,
      start,
      end,
      type,
    }: EventInteractionArgs<object> & { type: "drop" | "resize" }) => {
      const calendarEvent = event as UserEvent
      const newStart = typeof start === "string" ? parseISO(start) : start
      const newEnd = typeof end === "string" ? parseISO(end) : end
      await handleEventUpdate(calendarEvent, newStart, newEnd, type)
    },
    [handleEventUpdate],
  )

  const onEventDrop = (args: EventInteractionArgs<object>) =>
    handleEventInteraction({ ...args, type: "drop" })

  const onEventResize = (args: EventInteractionArgs<object>) =>
    handleEventInteraction({ ...args, type: "resize" })

  const onNavigate = useCallback(
    (date: Date) => {
      const newDate = parseISO(formatISO(date))
      const startOfWeekDate = startOfWeek(newDate)
      const endOfWeekDate = endOfWeek(newDate)

      if (
        isBefore(endOfWeekDate, parseISO(startDate)) ||
        isAfter(startOfWeekDate, parseISO(endDate)) ||
        isPending
      ) {
        return
      }

      setCurrentDate(extractISODate(newDate))
    },
    [setCurrentDate, startDate, endDate],
  )

  return {
    onEventDrop,
    onEventResize,
    onNavigate,
  }
}
